"use client";
import { Moon, Sun } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { cn } from "@/lib/utils";

interface AnimatedThemeTogglerProps
	extends React.ComponentPropsWithoutRef<"button"> {
	duration?: number;
}
export const AnimatedThemeToggler = ({
	className,
	duration = 400,
	...props
}: AnimatedThemeTogglerProps) => {
	const [isDark, setIsDark] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		const updateTheme = () => {
			setIsDark(document.documentElement.classList.contains("dark"));
		};
		updateTheme();
		const observer = new MutationObserver(updateTheme);
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["class"],
		});
		return () => observer.disconnect();
	}, []);
	const toggleTheme = useCallback(() => {
		const button = buttonRef.current;
		if (!button) return;
		const { top, left, width, height } = button.getBoundingClientRect();
		const x = left + width / 2;
		const y = top + height / 2;
		const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
		const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
		const maxRadius = Math.hypot(
			Math.max(x, viewportWidth - x),
			Math.max(y, viewportHeight - y),
		);
		const applyTheme = () => {
			const newTheme = !isDark;
			setIsDark(newTheme);
			document.documentElement.classList.toggle("dark");
			localStorage.setItem("theme", newTheme ? "dark" : "light");
		};
		if (typeof document.startViewTransition !== "function") {
			applyTheme();
			return;
		}
		const transition = document.startViewTransition(() => {
			flushSync(applyTheme);
		});
		const ready = transition?.ready;
		if (ready && typeof ready.then === "function") {
			ready.then(() => {
				document.documentElement.animate(
					{
						clipPath: [
							`circle(0px at ${x}px ${y}px)`,
							`circle(${maxRadius}px at ${x}px ${y}px)`,
						],
					},
					{
						duration,
						easing: "ease-in-out",
						pseudoElement: "::view-transition-new(root)",
					},
				);
			});
		}
	}, [isDark, duration]);
	return (
		<button
			type="button"
			ref={buttonRef}
			onClick={toggleTheme}
			className={cn(className)}
			{...props}
		>
			{isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
			<span className="sr-only">Toggle theme</span>
		</button>
	);
};
